import { mergeStyles } from '@cian/utils';
import isEqual from 'lodash/isEqual';
import memoize from 'lodash/memoize';
import * as React from 'react';

import { otherwiseChangingSlides, makeTransitionSetState, touchEndSetState, touchMoveSetState } from './callbacks';
import { getSlides } from './get_slide';
import styles from './index.css';
import { Preloader } from './preloader/preloader';
import { shallowEqual } from './react_helpers';
import { SlideSource } from './slide/slide';
import { ISliderProps, SliderState, RefFunction, ISlide, Direction, Transition, IContainerSize } from './types';
import {
  jumpToSlide,
  toSlideTransition,
  getCurrentSlide,
  getDistanceBetweenTwoCoords,
  getCoordsFromTouch,
  getCoordsFromTouches,
  normalizeZoom,
  getZoomTransition,
  getTouchOffsetCoords,
  getTransition,
} from './utils';
import { getYOffsetHanlder } from './utils/get_y_offset';
import { PlayerState } from './youtube';

export class Slider extends React.Component<ISliderProps, SliderState> {
  private containerSize?: IContainerSize;

  private setSlideSize = memoize(
    (slideIndex: number): RefFunction =>
      node => {
        if (!node) {
          return;
        }

        // otherwise changing slides from the first one does not work
        setTimeout(() => {
          this.setState(otherwiseChangingSlides(node, slideIndex));
        });
      },
  );

  public constructor(props: ISliderProps) {
    super(props);

    const { slides } = props;

    const getSlideFromSource = (source: SlideSource): ISlide => {
      const slide = source.slide;
      const isVideo = slide && Boolean(slide.video);

      return {
        source: {
          ...source,
          status: isVideo ? 'video' : source.status,
          onPlayerStateChange: isVideo ? this.handlePlayerStateChange : undefined,
        },
        zoom: 1,
      };
    };

    this.state = {
      slides: slides.map(getSlideFromSource),
      currentSlideIndex: 0,
      action: 'waiting',
      touchStartCoords: { x: 0, y: 0 },
      currentTouchCoords: { x: 0, y: 0 },
      offsetCoords: { x: 0, y: 0 },
      transition: undefined,
    };
  }

  public shouldComponentUpdate(nextProps: ISliderProps, nextState: SliderState) {
    return !isEqual(this.state, nextState) || !shallowEqual(this.props, nextProps);
  }

  public setSlide(index: number): void {
    this.setState(jumpToSlide(this.state, index));
  }

  public setSlideNext(index: number): void {
    const { state } = this;
    let newDirection: Direction = 'none';

    const isCurrentSlide = state.slides.length === 1 || state.currentSlideIndex === index;
    const isSwiping = state.touchStartCoords.x !== state.currentTouchCoords.x;

    if (!isCurrentSlide) {
      if (isSwiping) {
        // slides > 1
        if (state.touchStartCoords.x < state.currentTouchCoords.x) {
          newDirection = 'right';
        }

        if (state.touchStartCoords.x > state.currentTouchCoords.x) {
          newDirection = 'left';
        }
      } else if (state.slides.length === 2) {
        // slides == 2
        newDirection = 'left';
      } else {
        // slides > 2
        if (state.currentSlideIndex < index) {
          newDirection = 'left';
        } else {
          newDirection = 'right';
        }
      }
    }

    const transition = toSlideTransition(index, newDirection);
    const newState = { transition };

    this.setState(newState as SliderState);
  }

  public updateCurrentSlideZoom = (command: (zoom: number, state: SliderState) => number): void => {
    this.updateCurrentSlideZoomInState(command);
    this.makeTransition(getZoomTransition(Infinity));
  };

  public UNSAFE_componentWillUpdate(_: ISliderProps, nextState: SliderState) {
    this.handleSlideMove(nextState);
    this.handleSlideWillChange(nextState);
    this.handleZoomWillChange(nextState);
  }

  public render() {
    return this.state.slides.length === 0 ? (
      <Preloader />
    ) : (
      <div
        {...mergeStyles(styles['slider'], this.props.wrapperStyles, { height: this.props.height || 180 })}
        onTouchStart={this.touchStart}
        onTouchMove={this.touchMove}
        onTouchEnd={this.touchEnd}
        onClick={this.props.onClick}
        ref={this.setContainerSize}
      >
        {getSlides(this.state, this.containerSize, this.transitionEnd, this.setSlideSize, this.props.noInterface)}
      </div>
    );
  }

  private updateCurrentSlideZoomInState = (command: (zoom: number, state: SliderState) => number): void => {
    this.setState((state: SliderState) => {
      return {
        slides: state.slides.map((slide, index) => {
          return index === state.currentSlideIndex ? { ...slide, zoom: command(slide.zoom, state) } : slide;
        }),
      };
    });
  };

  private handlePlayerStateChange = (state: PlayerState) =>
    this.props.onPlayerStateChange && this.props.onPlayerStateChange(state);

  private setContainerSize = (node: HTMLDivElement | null) => {
    if (node) {
      this.containerSize = {
        height: node.clientHeight,
        width: node.clientWidth,
      };
    }
  };

  private transitionEnd = (stateCreater: (state: SliderState) => SliderState) => {
    if (this.state.action !== 'transition') {
      return;
    }
    if (this.props.onSlideTransitionEnd) {
      this.props.onSlideTransitionEnd();
    }

    return this.setState(stateCreater);
  };

  private isZoomTouch = (touches: React.TouchList | null) => {
    return !!touches && touches.length === 2 && getCurrentSlide(this.state).source.status !== 'video';
  };

  private touchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (this.isZoomTouch(e.touches)) {
      const startDistanceBetweenTouches = getDistanceBetweenTwoCoords(
        getCoordsFromTouch(e.touches[0]),
        getCoordsFromTouch(e.touches[1]),
      );
      const newState = (state: SliderState): SliderState => {
        const newState = {
          action: 'zoom',
          startDistanceBetweenTouches,
          startZoom: getCurrentSlide(state).zoom,
        };

        return newState as SliderState;
      };
      this.setState(newState);

      return;
    }

    const coords = getCoordsFromTouches(e.touches);
    if (!coords) {
      return;
    }

    this.setState({
      action: 'move',
      touchStartCoords: coords,
      currentTouchCoords: coords,
    });

    return;
  };

  private touchMove = (e: React.TouchEvent<HTMLElement>) => {
    e.preventDefault();

    if (this.isZoomTouch(e.touches)) {
      const distanceBetweenTouches = getDistanceBetweenTwoCoords(
        getCoordsFromTouch(e.touches[0]),
        getCoordsFromTouch(e.touches[1]),
      );

      this.updateCurrentSlideZoomInState((zoom, state) => {
        if (state.action !== 'zoom') {
          return zoom;
        }

        return normalizeZoom(state.startZoom * (distanceBetweenTouches / state.startDistanceBetweenTouches));
      });

      return;
    }

    const coords = getCoordsFromTouches(e.touches);
    if (!coords) {
      return;
    }

    this.setState(touchMoveSetState(coords, this.props.preventXMoving));
  };

  private touchEnd = () => {
    if (this.state.action === 'zoom') {
      this.makeTransition(getZoomTransition(Infinity));
    } else {
      this.setState(touchEndSetState(this.containerSize));
      if (this.props.onSlideMovingEnd) {
        this.props.onSlideMovingEnd();
      }
      this.makeTransition(getTransition);
    }
  };

  private handleSlideWillChange(state: SliderState) {
    if (
      state.action === 'transition' &&
      state.transition.type === 'to-slide' &&
      state.transition.slideNumber !== state.currentSlideIndex &&
      this.props.slideWillChange
    ) {
      this.props.slideWillChange(state.transition.slideNumber);
    }
  }

  private handleZoomWillChange(state: SliderState) {
    if (this.props.zoomWillChange) {
      this.props.zoomWillChange(getCurrentSlide(state).zoom);
    }
  }

  private handleSlideMove(state: SliderState) {
    if (this.props.onSlideMove && state.action === 'move') {
      const coords = getTouchOffsetCoords(state);
      this.props.onSlideMove(coords, { x: coords.x, y: getYOffsetHanlder(state, this.containerSize) });
    }
  }

  private makeTransition(getTransition: (state: SliderState, containerSize?: IContainerSize) => Transition) {
    this.setState(makeTransitionSetState(getTransition, this.containerSize));
  }
}
